import useFetch from "hooks/useFetch";
import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Icon,
  Select,
  TextField,
  styles as ui,
  useToast,
} from "stories/Admin";
import { PrayerTimes } from "types/JamatPageTypes";
import OffsetModal from "../PrayerTimeRow/OffsetModal/OffsetModal";
import SlidersIcon from "../SlidersIcon";
import {
  formatDdMm,
  formatPeriodLabel,
  formatShortPeriodLabel,
  getDatesInPeriod,
  getDaysInMonth,
  MONTHS_LONG,
} from "../jamatPeriodUtils";
import styles from "./JamatPeriodForm.module.scss";

const EMPTY_TIMES: PrayerTimes = {
  fajr: "",
  duhr: "",
  asr: "",
  maghrib: "",
  isha: "",
  fajrJamatOffset: -1,
  duhrJamatOffset: -1,
  asrJamatOffset: -1,
  maghribJamatOffset: -1,
  ishaJamatOffset: -1,
};

interface JamatPeriodFormProps {
  mosqueOrgNr: string;
  onSaved: () => void;
}

const JamatPeriodForm: React.FC<JamatPeriodFormProps> = ({
  mosqueOrgNr,
  onSaved,
}) => {
  const { execute, isLoading } = useFetch();
  const toast = useToast();
  const [fromDay, setFromDay] = useState(1);
  const [fromMonth, setFromMonth] = useState(1);
  const [toDay, setToDay] = useState(1);
  const [toMonth, setToMonth] = useState(1);
  const [times, setTimes] = useState<PrayerTimes>(EMPTY_TIMES);
  const [isModalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fromDate = formatDdMm(fromDay, fromMonth);
  const toDate = formatDdMm(toDay, toMonth);
  const periodDates = useMemo(
    () => getDatesInPeriod(fromDate, toDate),
    [fromDate, toDate],
  );
  const periodLabel = formatPeriodLabel(fromDate, toDate);

  const hasValidTimes = () =>
    ["fajr", "duhr", "asr", "maghrib", "isha"].some(
      (prayer) =>
        times[prayer as keyof PrayerTimes] !== "" &&
        times[prayer as keyof PrayerTimes] !== "00:00",
    );

  const handlePrayerTimeChange = (
    prayer: "fajr" | "duhr" | "asr" | "maghrib" | "isha",
    value: string,
  ) => {
    const cleanedValue = value.replace(/[^\d:]/g, "");

    if (cleanedValue.length === 2 && !cleanedValue.includes(":")) {
      setTimes((prev) => ({ ...prev, [prayer]: cleanedValue + ":" }));
    } else if (/^(\d{0,2}):?(\d{0,2})$/.test(cleanedValue)) {
      setTimes((prev) => ({ ...prev, [prayer]: cleanedValue }));
    }
  };

  const handleTimeBlur = (
    prayer: "fajr" | "duhr" | "asr" | "maghrib" | "isha",
    value: string,
  ) => {
    if (value === "") {
      setTimes((prev) => ({ ...prev, [prayer]: "" }));
    } else if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(value)) {
      setTimes((prev) => ({ ...prev, [prayer]: "00:00" }));
    }
  };

  const handleOffsetChange = (
    _dateKey: string,
    offset:
      | "fajrJamatOffset"
      | "duhrJamatOffset"
      | "asrJamatOffset"
      | "maghribJamatOffset"
      | "ishaJamatOffset",
    value: number,
  ) => {
    setTimes((prev) => ({ ...prev, [offset]: value }));
  };

  const isInputDisabled = (time: string, offset?: number) =>
    time !== "" && time !== "00:00" && offset !== undefined && offset !== -1;

  const handleSavePeriod = async () => {
    setError(null);

    if (!hasValidTimes()) {
      setError("Fyll inn minst én jamat-tid.");
      return;
    }

    const payload = periodDates.map((startDate) => ({
      id: 0,
      startDate,
      endDate: startDate,
      fajr: times.fajr,
      duhr: times.duhr,
      asr: times.asr,
      maghrib: times.maghrib,
      isha: times.isha,
      mosqueOrgNr,
      fajrJamatOffset: times.fajrJamatOffset ?? -1,
      duhrJamatOffset: times.duhrJamatOffset ?? -1,
      asrJamatOffset: times.asrJamatOffset ?? -1,
      maghribJamatOffset: times.maghribJamatOffset ?? -1,
      ishaJamatOffset: times.ishaJamatOffset ?? -1,
    }));

    try {
      await execute("/api/v1/jamat/batch", "POST", payload);
      setTimes(EMPTY_TIMES);
      onSaved();
      toast.success(
        `Jamat-tidene er lagret for ${periodDates.length} dager (${periodLabel}).`,
      );
    } catch {
      setError("Kunne ikke lagre jamat-tidene. Prøv igjen.");
    }
  };

  const dayOptions = (month: number) =>
    Array.from({ length: getDaysInMonth(month) }, (_, index) => ({
      value: index + 1,
      label: (index + 1).toString().padStart(2, "0"),
    }));

  const monthOptions = MONTHS_LONG.map((name, index) => ({
    value: index + 1,
    label: name,
  }));

  const clampDay = (day: number, month: number) =>
    Math.min(day, getDaysInMonth(month));

  return (
    <Card
      title="Legg inn for en periode"
      subtitle="Velg dag og måned. Året brukes ikke."
    >
      <div className={styles.dateRange}>
        <fieldset className={styles.dateGroup}>
          <legend className={ui.lbl}>Fra</legend>
          <div className={styles.dateSelectors}>
            <Select
              aria-label="Fra dag"
              className={`${ui.num} ${styles.day}`}
              value={fromDay}
              onChange={(e) => setFromDay(parseInt(e.target.value, 10))}
              options={dayOptions(fromMonth)}
            />
            <Select
              aria-label="Fra måned"
              className={styles.month}
              value={fromMonth}
              onChange={(e) => {
                const month = parseInt(e.target.value, 10);
                setFromMonth(month);
                setFromDay((day) => clampDay(day, month));
              }}
              options={monthOptions}
            />
          </div>
        </fieldset>

        <span className={styles.arrow} aria-hidden="true">
          <Icon name="chevronRight" size={18} strokeWidth={2} />
        </span>

        <fieldset className={styles.dateGroup}>
          <legend className={ui.lbl}>Til</legend>
          <div className={styles.dateSelectors}>
            <Select
              aria-label="Til dag"
              className={`${ui.num} ${styles.day}`}
              value={toDay}
              onChange={(e) => setToDay(parseInt(e.target.value, 10))}
              options={dayOptions(toMonth)}
            />
            <Select
              aria-label="Til måned"
              className={styles.month}
              value={toMonth}
              onChange={(e) => {
                const month = parseInt(e.target.value, 10);
                setToMonth(month);
                setToDay((day) => clampDay(day, month));
              }}
              options={monthOptions}
            />
          </div>
        </fieldset>

        <Badge tone="mint" className={styles.periodBadge}>
          <Icon name="cal" size={14} strokeWidth={2} />
          {formatShortPeriodLabel(fromDate, toDate)} · {periodDates.length}{" "}
          dager
        </Badge>
      </div>

      <div className={styles.timesRow}>
        {(["fajr", "duhr", "asr", "maghrib", "isha"] as const).map((prayer) => {
          const offsetKey = `${prayer}JamatOffset` as keyof PrayerTimes;
          const offset = times[offsetKey] as number | undefined;
          const label =
            prayer === "duhr"
              ? "Dhuhr"
              : prayer.charAt(0).toUpperCase() + prayer.slice(1);
          const disabled = isInputDisabled(times[prayer], offset);

          return disabled ? (
            <div key={prayer} className={ui.fld}>
              <span className={ui.lbl}>{label}</span>
              <span
                className={`${ui.inp} ${styles.time} ${styles.offset}`}
                aria-label={`${label}: ${offset} minutter etter bønnetiden`}
              >
                +{offset} min
              </span>
            </div>
          ) : (
            <TextField
              key={prayer}
              label={label}
              inputMode="numeric"
              className={`${ui.num} ${styles.time}`}
              value={
                times[prayer] === "00:00" || times[prayer] === ""
                  ? ""
                  : times[prayer]
              }
              onChange={(e) => handlePrayerTimeChange(prayer, e.target.value)}
              onBlur={(e) => handleTimeBlur(prayer, e.target.value)}
              placeholder="tt:mm"
            />
          );
        })}
        <span className={styles.actions}>
          <Button
            variant="secondary"
            icon={<SlidersIcon />}
            onClick={() => setModalOpen(true)}
          >
            Bruk offset
          </Button>
          <Button
            icon={<Icon name="check" size={16} strokeWidth={2.2} />}
            onClick={handleSavePeriod}
            loading={isLoading}
          >
            {isLoading ? "Lagrer …" : "Lagre perioden"}
          </Button>
        </span>
      </div>

      {error && <Alert tone="error">{error}</Alert>}

      <span className={styles.help}>
        Samme tider gjelder alle dagene og overskriver eksisterende tider.
        Perioden kan gå over nyttår (f.eks. desember–februar). Fyll inn minst én
        tid.
      </span>

      <OffsetModal
        show={isModalOpen}
        formattedDate={periodLabel}
        times={times}
        dateKey="period"
        onInputChange={handleOffsetChange}
        onSave={() => {}}
        onClose={() => setModalOpen(false)}
        fetchLatestPrayerTimes={() => {}}
      />
    </Card>
  );
};

export default JamatPeriodForm;
